"use client"

import { Fragment } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import type { Nullish } from "~/@types/generics"
import { FACETS } from "~/lib/algolia/constants"
import { DISCOUNT_PERCENT_CHAR, DISCOUNT_REPLACER } from "~/lib/algolia/object-serializers/utils/process-discount"
import { SIZES_SEPARATOR } from "~/lib/algolia/object-serializers/utils/process-sizes"
import type { COLOR_LABELS } from "~/lib/shopify/constants"
import { ColorTranslation } from "~/components/ui/ColorTranslation"
import { getFiltersAmount } from "~/components/ui/Panels/Filters/utils/get-filters-amount"
import Icon from "~/components/abstracts/Icon"
import { isObject } from "~/utils/is-object"
import { objectEntries } from "~/utils/object-entries"
import { objectValues } from "~/utils/object-values"

import * as css from "./style.css"

type SelectedFilter = Record<string, string | boolean>
type SelectedFilters = Record<string, SelectedFilter>

export function FilterPills() {
  const { setValue } = useFormContext()
  const watchedValues = useWatch()

  const currentlySelectedFiltersAmount = getFiltersAmount(watchedValues)

  if (currentlySelectedFiltersAmount === 0) {
    return null
  }

  const currentlySelectedFilters = watchedValues as SelectedFilters

  const pills = objectEntries(currentlySelectedFilters ?? {})
    ?.filter(([key]) => {
      return key !== "sort"
    })
    ?.filter(([_, values]) => {
      return values && typeof values !== "boolean" && (isObject(values) || typeof values === "string")
    })
    ?.map(([key, values]) => {
      if (values && typeof values === "string") {
        return [key, [values]]
      }

      return [key, values]
    })

  if (!pills?.length) {
    return null
  }

  function handleClear(key: string, value: string) {
    const values = currentlySelectedFilters?.[key]
    const currentValue = values?.[value] ?? null

    if (!currentValue) {
      return
    }

    const clonedValues = { ...values, [value]: false }

    setValue(key, clonedValues)
  }

  const renderPillContent = (key: Nullish<(typeof pills)[0][0]>, value: string) => {
    const isColor = FACETS.COLOR === key
    const isSize = FACETS.ACCESSORIES_SIZE === key || FACETS.STANDARD_SIZE === key
    const isDiscount = FACETS.DISCOUNT === key

    if (isColor) {
      return <ColorTranslation tKey={value as keyof typeof COLOR_LABELS} />
    }

    if (isSize) {
      return value.replace(SIZES_SEPARATOR, "")
    }

    if (isDiscount) {
      return `${value.replaceAll(DISCOUNT_REPLACER, DISCOUNT_PERCENT_CHAR)}`
    }

    return value
  }

  return (
    <ul className={css.FilterPills}>
      {pills?.map(([key, values]) => {
        if (isObject(values)) {
          return (
            <Fragment key={`filterPill-${key}`}>
              {objectValues(values)
                ?.filter((value) => typeof value !== "boolean")
                ?.map((value) => {
                  return value ? (
                    <li key={`filterPill-${key}-${value}`} className={css.pill}>
                      <span>{renderPillContent(key, value as string)}</span>
                      <Icon
                        className={css.icon}
                        name="Close"
                        onClick={() => handleClear(key as string, value as string)}
                      />
                    </li>
                  ) : null
                })}
            </Fragment>
          )
        }

        return null
      })}
    </ul>
  )
}
